import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Monitor, Moon, Sun, Trash2, Upload } from 'lucide-react';
import type { DesktopSettings, DesktopUpdateState, NameFont, SelfUser, SessionInfo, UserSettings as Settings } from '@spawnwire/shared';
import { useImageCropper } from '../../components/ImageCropper';
import { Avatar, Button, clsx, colorFor, Field, Input, Kbd, ProfileBanner, Select, Spinner, Switch, TextArea } from '../../components/ui';
import { errorMessage, http, uploadFile } from '../../lib/api';
import { attempt } from '../../lib/data';
import { desktop } from '../../lib/desktop';
import { micLabel } from '../../lib/local';
import { native } from '../../lib/native';
import { LinkifiedText } from '../../lib/markdown';
import { NAME_FONT_OPTIONS, NameText } from '../../lib/nameFonts';
import { notificationsSupported, playChime, requestNotificationPermission } from '../../lib/notifications';
import { formatTimestamp } from '../../lib/time';
import { refreshOutputs, restartMicrophone, openDevice, setInputVolume, startMicTest } from '../../lib/voice';
import { actions, useStore } from '../../store/store';
import { ui } from '../../store/ui';
import { SHORTCUTS } from '../shell/shortcuts';
import { AccountStandingSection } from '../standing/Standing';
import { logout } from '../shell/UserPanel';
import { TagChip } from '../members/SpaceTag';
import { PhoneNotifications } from './PhoneNotifications';
import { Section, SettingsLayout } from './SettingsLayout';
import { WornTagPicker } from './SpaceTagSettings';

// Few people open it, so it isn't in the main bundle.
const DeveloperPortal = lazy(() => import('../developer/DeveloperPortal'));

type SettingsPatch = Partial<Omit<Settings, 'audio'>> & { audio?: Partial<Settings['audio']> };

export async function saveSettings(patch: SettingsPatch): Promise<void> {
  const me = useStore.getState().me;
  if (!me) return;
  // Apply immediately so toggles feel instant; the server response confirms.
  actions.setMe({ ...me, settings: { ...me.settings, ...patch, audio: { ...me.settings.audio, ...(patch.audio ?? {}) } } as Settings });
  try {
    const updated = await http.patch<SelfUser>('/users/@me/settings', patch);
    actions.setMe(updated);
  } catch (err) {
    actions.setMe(me);
    ui.toast(errorMessage(err), 'error');
  }
}

/**
 * Picks the microphone and remembers it: the id on your account and the name in this browser. If it isn't
 * connected, the browser's default microphone is used until it is. Swaps it straight away in a call.
 */
export async function chooseMicrophone(device: { deviceId: string; label: string } | null): Promise<void> {
  micLabel.set(device?.label || null);
  await saveSettings({ audio: { inputDeviceId: device?.deviceId ?? null } });
  await restartMicrophone();
}

function useSettings(): Settings {
  return useStore((s) => s.me!.settings);
}

function Account() {
  const me = useStore((s) => s.me!);
  const [username, setUsername] = useState(me.username);
  const [email, setEmail] = useState(me.email);
  const [newPassword, setNewPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessions, setSessions] = useState<SessionInfo[] | null>(null);
  const [deletePassword, setDeletePassword] = useState('');

  const loadSessions = () => void http.get<SessionInfo[]>('/users/@me/sessions').then(setSessions).catch(() => setSessions([]));
  useEffect(loadSessions, []);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const updated = await http.patch<SelfUser>('/users/@me/account', {
        currentPassword,
        username: username !== me.username ? username : undefined,
        email: email !== me.email ? email : undefined,
        newPassword: newPassword || undefined,
      });
      actions.setMe(updated);
      setCurrentPassword('');
      setNewPassword('');
      ui.toast('Account updated.', 'success');
      loadSessions();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const dirty = username !== me.username || email !== me.email || !!newPassword;
  return (
    <>
      <Section title="Sign-in details" description="Changing any of these needs your current password. A new password signs out your other sessions.">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Username" htmlFor="acc-user">
            <Input id="acc-user" value={username} onChange={(e) => setUsername(e.target.value.toLowerCase())} autoComplete="username" />
          </Field>
          <Field label="Email" htmlFor="acc-email">
            <Input id="acc-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
          </Field>
          <Field label="New password" htmlFor="acc-new" hint="Leave empty to keep your current password.">
            <Input id="acc-new" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} autoComplete="new-password" />
          </Field>
          <Field label="Current password" htmlFor="acc-cur">
            <Input id="acc-cur" type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} autoComplete="current-password" />
          </Field>
        </div>
        {error && <p className="text-sm text-danger">{error}</p>}
        <Button disabled={!dirty || !currentPassword} loading={busy} onClick={() => void save()}>
          Save changes
        </Button>
      </Section>

      <Section title="Sessions" description="Devices currently signed in to your account.">
        {!sessions ? (
          <p className="text-sm text-muted">Loading sessions</p>
        ) : (
          <ul className="divide-y divide-line rounded-lg border border-line">
            {sessions.map((s) => (
              <li key={s.id} className="flex items-center gap-3 px-4 py-3">
                <Monitor className="size-5 shrink-0 text-muted" aria-hidden />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm text-fg">{s.userAgent || 'Unknown device'}</div>
                  <div className="text-xs text-faint">
                    {s.current ? 'This device' : `Last active ${formatTimestamp(s.lastUsedAt)}`} {'·'} signed in {formatTimestamp(s.createdAt)}
                  </div>
                </div>
                {!s.current && (
                  <Button size="sm" variant="secondary" onClick={() => void attempt(() => http.del(`/users/@me/sessions/${s.id}`)).then(loadSessions)}>
                    Sign out
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Delete account" description="Your profile, friends, memberships and settings are removed. Messages you sent stay in place, shown as from a deleted user. You must transfer or delete spaces you own first. This cannot be undone.">
        <div className="flex flex-col gap-2 sm:flex-row">
          <Input type="password" placeholder="Your password" value={deletePassword} onChange={(e) => setDeletePassword(e.target.value)} aria-label="Password to confirm account deletion" autoComplete="current-password" />
          <Button
            variant="danger"
            icon={Trash2}
            disabled={!deletePassword}
            onClick={() =>
              ui.confirm({
                title: 'Delete your account?',
                body: 'This is permanent. You will be signed out everywhere.',
                confirmLabel: 'Delete account',
                danger: true,
                onConfirm: async () => {
                  await http.del('/users/@me', { password: deletePassword });
                  actions.reset();
                },
              })
            }
          >
            Delete account
          </Button>
        </div>
      </Section>
    </>
  );
}

/** Upload / remove control for a profile image (avatar or banner). Uploads apply immediately. */
function ProfileImageField({ kind, label, hint, hasImage }: { kind: 'avatar' | 'banner'; label: string; hint?: string; hasImage: boolean }) {
  const [uploading, setUploading] = useState<number | null>(null);
  const file = useRef<HTMLInputElement>(null);
  const cropper = useImageCropper(kind, (f) => {
    setUploading(0);
    uploadFile<SelfUser>(`/users/@me/${kind}`, f, setUploading)
      .promise.then((u) => actions.setMe(u))
      .catch((err) => ui.toast(errorMessage(err), 'error'))
      .finally(() => setUploading(null));
  });
  return (
    <Field label={label} hint={hint}>
      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" icon={Upload} loading={uploading !== null} onClick={() => file.current?.click()}>
          {uploading !== null ? `Uploading ${Math.round(uploading * 100)}%` : 'Upload image'}
        </Button>
        {hasImage && (
          <Button variant="ghost" onClick={() => void attempt(() => http.del<SelfUser>(`/users/@me/${kind}`)).then((u) => u && actions.setMe(u))}>
            Remove
          </Button>
        )}
      </div>
      <input
        ref={file}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp,image/avif"
        hidden
        aria-label={label}
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (f) cropper.pick(f);
        }}
      />
      {cropper.dialog}
    </Field>
  );
}

function Profile() {
  const me = useStore((s) => s.me!);
  const [displayName, setDisplayName] = useState(me.displayName);
  const [pronouns, setPronouns] = useState(me.pronouns);
  const [bio, setBio] = useState(me.bio);
  const [banner, setBanner] = useState(me.bannerColor ?? colorFor(me.id));
  const [nameFont, setNameFont] = useState<NameFont | null>(me.nameFont);
  const dirty =
    displayName !== me.displayName || pronouns !== me.pronouns || bio !== me.bio || banner !== (me.bannerColor ?? colorFor(me.id)) || nameFont !== me.nameFont;

  return (
    <div className="grid gap-8 lg:grid-cols-[1fr_280px]">
      <div className="space-y-5">
        <Field label="Display name" htmlFor="pf-name">
          <Input id="pf-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} maxLength={32} />
        </Field>
        <Field label="Name font" hint="How your display name looks in chat, the member list and your profile.">
          <div role="radiogroup" aria-label="Name font" className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {NAME_FONT_OPTIONS.map((o) => (
              <button
                key={o.label}
                type="button"
                role="radio"
                aria-checked={nameFont === o.value}
                onClick={() => setNameFont(o.value)}
                className={clsx(
                  'flex min-w-0 flex-col items-start gap-1 rounded-lg border px-3 py-2 text-left transition-colors',
                  nameFont === o.value ? 'border-accent bg-accent-soft' : 'border-line bg-input hover:border-line-strong',
                )}
              >
                <span className="w-full truncate text-base font-semibold text-fg">
                  <NameText font={o.value}>{displayName || me.username}</NameText>
                </span>
                <span className="text-xs text-muted">{o.label}</span>
              </button>
            ))}
          </div>
        </Field>
        <WornTagPicker />
        <Field label="Pronouns" htmlFor="pf-pro">
          <Input id="pf-pro" value={pronouns} onChange={(e) => setPronouns(e.target.value)} maxLength={40} placeholder="Add your pronouns" />
        </Field>
        <Field label="About me" htmlFor="pf-bio" hint={`${190 - bio.length} characters left`}>
          <TextArea id="pf-bio" rows={4} value={bio} onChange={(e) => setBio(e.target.value)} maxLength={190} />
        </Field>
        <ProfileImageField kind="avatar" label="Avatar" hint="You can crop it after picking. GIFs and other animated images upload as they are." hasImage={!!me.avatarUrl} />
        <ProfileImageField
          kind="banner"
          label="Banner image"
          hint="A photo or picture across the top of your profile, cropped to 3:1. GIFs and other animated images upload as they are and keep moving."
          hasImage={!!me.bannerUrl}
        />
        <Field label="Banner colour" htmlFor="pf-banner" hint={me.bannerUrl ? 'Shown while your banner image loads, or if you remove it.' : undefined}>
          <input id="pf-banner" type="color" value={banner} onChange={(e) => setBanner(e.target.value)} className="h-10 w-20 cursor-pointer rounded border border-line bg-transparent" />
        </Field>
        <Button
          disabled={!dirty || !displayName.trim()}
          onClick={() =>
            void attempt(() => http.patch<SelfUser>('/users/@me', { displayName, pronouns, bio, bannerColor: banner, nameFont }), 'Profile saved.').then((u) => u && actions.setMe(u))
          }
        >
          Save profile
        </Button>
      </div>
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-muted">Preview</div>
        <div className="mt-2 overflow-hidden rounded-xl border border-line bg-sidebar">
          <ProfileBanner user={me} color={banner} className={me.bannerUrl ? 'h-24' : 'h-16'} />
          <div className="px-4 pb-4">
            <div className="-mt-8 inline-block rounded-full ring-4 ring-sidebar">
              <Avatar user={me} size={64} />
            </div>
            <div className="mt-2 flex min-w-0 items-center gap-1.5">
              <span className="truncate font-bold text-fg">
                <NameText font={nameFont}>{displayName || me.username}</NameText>
              </span>
              {me.tag && <TagChip tag={me.tag} />}
            </div>
            <div className="text-xs text-muted">
              @{me.username}
              {pronouns ? ` · ${pronouns}` : ''}
            </div>
            {bio && (
              <p className="mt-2 whitespace-pre-wrap text-sm text-fg [overflow-wrap:anywhere]">
                <LinkifiedText text={bio} />
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Privacy() {
  const s = useSettings();
  return (
    <>
      <Section title="Direct messages" description="Who can start a new conversation with you. Friends can always message you.">
        <Select
          value={s.dmPolicy}
          onChange={(dmPolicy) => void saveSettings({ dmPolicy })}
          options={[
            { value: 'everyone', label: 'Anyone who can find me' },
            { value: 'space_members', label: 'People who share a space with me' },
            { value: 'friends', label: 'Friends only' },
          ]}
        />
      </Section>
      <Section title="Friend requests" description="Who can send you a friend request.">
        <Select
          value={s.friendRequestPolicy}
          onChange={(friendRequestPolicy) => void saveSettings({ friendRequestPolicy })}
          options={[
            { value: 'everyone', label: 'Everyone' },
            { value: 'mutual', label: 'People with a mutual space or friend' },
            { value: 'nobody', label: 'Nobody' },
          ]}
        />
      </Section>
      <Section title="Blocked people" description="Blocked people cannot message you, call you or send friend requests. Their messages in spaces are hidden. They are not told.">
        <Switch checked={s.showBlockedMessages} onChange={(v) => void saveSettings({ showBlockedMessages: v })} label="Show messages from blocked people" description="Off by default: their messages are collapsed and can be revealed one at a time." />
        <p className="text-sm text-muted">Manage the list under Friends, Blocked.</p>
      </Section>
    </>
  );
}

/** In the Windows app, notifications are a setting of this computer and need no browser permission. */
function WindowsNotifications() {
  const bridge = desktop!;
  const [on, setOn] = useState<boolean | null>(null);
  useEffect(() => void bridge.getSettings().then((d) => setOn(d.notifications)), [bridge]);
  if (on === null) return <Spinner />;
  return (
    <Section title="Desktop notifications" description="Windows notifications for direct messages and mentions (or every message, per space and channel settings) on this computer.">
      <Switch
        checked={on}
        onChange={(notifications) => {
          setOn(notifications);
          void bridge.updateSettings({ notifications }).then((d) => setOn(d.notifications));
        }}
        label="Enable desktop notifications"
      />
      {on && (
        <Button variant="secondary" size="sm" onClick={() => bridge.notify({ id: `test-${Date.now()}`, tag: 'test', title: 'Spawnwire', body: 'Notifications are working.', path: '/app/@me' })}>
          Send a test notification
        </Button>
      )}
    </Section>
  );
}

function Notifications() {
  const s = useSettings();
  const [permission, setPermission] = useState(() => (notificationsSupported() ? Notification.permission : 'denied'));
  return (
    <>
      {native ? (
        <PhoneNotifications />
      ) : desktop ? (
        <WindowsNotifications />
      ) : (
        <Section title="Desktop notifications" description="Shown for direct messages and mentions (or every message, per space and channel settings) while Spawnwire is in the background.">
          <Switch
            checked={s.desktopNotifications && permission === 'granted'}
            disabled={!notificationsSupported()}
            onChange={async (on) => {
              if (on) {
                const p = await requestNotificationPermission();
                setPermission(p);
                if (p !== 'granted') {
                  ui.toast('Your browser blocked notifications. Allow them in the site settings to turn this on.', 'error');
                  return;
                }
              }
              void saveSettings({ desktopNotifications: on });
            }}
            label="Enable desktop notifications"
            description={!notificationsSupported() ? 'This browser does not support notifications.' : permission === 'denied' ? 'Blocked by the browser.' : undefined}
          />
          {permission === 'granted' && (
            <Button variant="secondary" size="sm" onClick={() => new Notification('Spawnwire', { body: 'Notifications are working.', icon: '/favicon.svg' })}>
              Send a test notification
            </Button>
          )}
        </Section>
      )}
      <Section title="Sounds">
        <Switch checked={s.notificationSounds} onChange={(v) => void saveSettings({ notificationSounds: v })} label="Notification sounds" description="Message chimes and call join and leave sounds." />
        <Button variant="secondary" size="sm" onClick={() => playChime('message')}>
          Play a sample
        </Button>
      </Section>
      <Section title="Per space and channel" description={`${native ? 'Long-press' : 'Right-click'} a space or channel to mute it. Do not disturb status silences everything.`}>
        <span />
      </Section>
    </>
  );
}

function Appearance() {
  const s = useSettings();
  const themes = [
    { value: 'dark' as const, label: 'Dark', icon: Moon },
    { value: 'light' as const, label: 'Light', icon: Sun },
    { value: 'system' as const, label: 'Match system', icon: Monitor },
  ];
  return (
    <>
      <Section title="Theme">
        <div className="grid grid-cols-3 gap-3">
          {themes.map((t) => (
            <button key={t.value} type="button" onClick={() => void saveSettings({ theme: t.value })} aria-pressed={s.theme === t.value} className={clsx('flex flex-col items-center gap-2 rounded-xl border-2 p-4 text-sm', s.theme === t.value ? 'border-accent text-fg' : 'border-line text-muted hover:border-line-strong')}>
              <t.icon className="size-6" aria-hidden />
              {t.label}
            </button>
          ))}
        </div>
      </Section>
      <Section title="Message display">
        <Select value={s.density} onChange={(density) => void saveSettings({ density })} options={[{ value: 'cozy', label: 'Cozy: avatars and spacing' }, { value: 'compact', label: 'Compact: more messages on screen' }]} />
        <Switch checked={s.use24Hour} onChange={(v) => void saveSettings({ use24Hour: v })} label="24-hour time" />
      </Section>
      <Section title="Text size">
        <div className="flex items-center gap-4">
          <input type="range" min={80} max={150} step={5} value={s.fontScale} onChange={(e) => void saveSettings({ fontScale: Number(e.target.value) })} className="w-full accent-[var(--p-accent)]" aria-label="Text size" />
          <span className="w-12 text-right text-sm text-muted">{s.fontScale}%</span>
        </div>
      </Section>
    </>
  );
}

function AccessibilitySection() {
  const s = useSettings();
  return (
    <>
      <Section title="Motion">
        <Switch checked={s.reducedMotion} onChange={(v) => void saveSettings({ reducedMotion: v })} label="Reduce motion" description="Turns off animations and transitions. Spawnwire also follows your system's reduced-motion setting." />
      </Section>
      <Section title="Contrast and links">
        <Switch checked={s.highContrast} onChange={(v) => void saveSettings({ highContrast: v })} label="Higher contrast" description="Stronger text and border colours." />
        <Switch checked={s.underlineLinks} onChange={(v) => void saveSettings({ underlineLinks: v })} label="Always underline links" />
      </Section>
      <Section title="Content">
        <Switch checked={s.showEmbeds} onChange={(v) => void saveSettings({ showEmbeds: v })} label="Show link previews" />
      </Section>
    </>
  );
}

function useDevices(): MediaDeviceInfo[] {
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  useEffect(() => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    const load = () => void navigator.mediaDevices.enumerateDevices().then(setDevices).catch(() => {});
    load();
    navigator.mediaDevices.addEventListener('devicechange', load);
    return () => navigator.mediaDevices.removeEventListener('devicechange', load);
  }, []);
  return devices;
}

function VoiceVideo() {
  const s = useSettings();
  const devices = useDevices();
  const [level, setLevel] = useState(0);
  const [testing, setTesting] = useState(false);
  const stopRef = useRef<(() => void) | null>(null);
  const [preview, setPreview] = useState<MediaStream | null>(null);
  const video = useRef<HTMLVideoElement>(null);
  const [mediaError, setMediaError] = useState<string | null>(null);

  useEffect(() => () => stopRef.current?.(), []);
  useEffect(() => () => preview?.getTracks().forEach((t) => t.stop()), [preview]);
  useEffect(() => {
    if (video.current) video.current.srcObject = preview;
  }, [preview]);

  const inputs = devices.filter((d) => d.kind === 'audioinput');
  const outputs = devices.filter((d) => d.kind === 'audiooutput');
  const cams = devices.filter((d) => d.kind === 'videoinput');
  const opts = (list: MediaDeviceInfo[], fallback: string) => [{ value: '', label: 'System default' }, ...list.filter((d) => d.deviceId && d.deviceId !== 'default').map((d, i) => ({ value: d.deviceId, label: d.label || `${fallback} ${i + 1}` }))];
  const audio = (patch: Partial<Settings['audio']>) => void saveSettings({ audio: patch });

  async function toggleTest() {
    if (testing) {
      stopRef.current?.();
      stopRef.current = null;
      setTesting(false);
      setLevel(0);
      return;
    }
    try {
      stopRef.current = await startMicTest(setLevel);
      setTesting(true);
      setMediaError(null);
    } catch {
      setMediaError('Could not open the microphone. Check that it is connected and that the browser has permission.');
    }
  }

  return (
    <>
      <Section title="Devices" description={devices.length > 0 && !devices[0]?.label ? 'Device names appear after you allow microphone or camera access.' : undefined}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Input device" htmlFor="vv-in">
            <Select id="vv-in" value={s.audio.inputDeviceId ?? ''} onChange={(v) => void chooseMicrophone(inputs.find((d) => d.deviceId === v) ?? null)} options={opts(inputs, 'Microphone')} />
          </Field>
          <Field label="Output device" htmlFor="vv-out" hint={'setSinkId' in HTMLMediaElement.prototype ? undefined : 'This browser always uses the system output.'}>
            <Select id="vv-out" value={s.audio.outputDeviceId ?? ''} onChange={(v) => { audio({ outputDeviceId: v || null }); setTimeout(refreshOutputs, 50); }} options={opts(outputs, 'Speaker')} />
          </Field>
          <Field label="Camera" htmlFor="vv-cam">
            <Select id="vv-cam" value={s.audio.videoDeviceId ?? ''} onChange={(v) => audio({ videoDeviceId: v || null })} options={opts(cams, 'Camera')} />
          </Field>
        </div>
      </Section>
      <Section title="Volume">
        <Field label={`Input volume (${s.audio.inputVolume}%)`}>
          <input type="range" min={0} max={200} value={s.audio.inputVolume} onChange={(e) => { const v = Number(e.target.value); audio({ inputVolume: v }); setInputVolume(v); }} className="w-full accent-[var(--p-accent)]" aria-label="Input volume" />
        </Field>
        <Field label={`Output volume (${s.audio.outputVolume}%)`}>
          <input type="range" min={0} max={200} value={s.audio.outputVolume} onChange={(e) => { audio({ outputVolume: Number(e.target.value) }); setTimeout(refreshOutputs, 50); }} className="w-full accent-[var(--p-accent)]" aria-label="Output volume" />
        </Field>
      </Section>
      <Section title="Mic test and sensitivity" description="Speak normally. The bar turns green above the threshold, which is when others see you as speaking.">
        <div className="flex items-center gap-3">
          <Button variant="secondary" onClick={() => void toggleTest()}>
            {testing ? 'Stop test' : "Let's check"}
          </Button>
          <div className="relative h-3 flex-1 overflow-hidden rounded-full bg-active" aria-label="Microphone level" role="meter" aria-valuenow={level} aria-valuemin={0} aria-valuemax={100}>
            <div className={clsx('h-full transition-[width] duration-75', level > s.audio.speakingThreshold ? 'bg-success' : 'bg-warning')} style={{ width: `${level}%` }} />
            <div className="absolute top-0 h-full w-0.5 bg-fg" style={{ left: `${s.audio.speakingThreshold}%` }} aria-hidden />
          </div>
        </div>
        <Field label={`Speaking threshold (${s.audio.speakingThreshold})`}>
          <input type="range" min={1} max={60} value={s.audio.speakingThreshold} onChange={(e) => audio({ speakingThreshold: Number(e.target.value) })} className="w-full accent-[var(--p-accent)]" aria-label="Speaking threshold" />
        </Field>
        {mediaError && <p className="text-sm text-danger">{mediaError}</p>}
      </Section>
      <Section title="Processing" description="Applied the next time you join a call.">
        <Switch checked={s.audio.echoCancellation} onChange={(v) => audio({ echoCancellation: v })} label="Echo cancellation" />
        <Switch checked={s.audio.noiseSuppression} onChange={(v) => audio({ noiseSuppression: v })} label="Noise suppression" />
        <Switch checked={s.audio.autoGainControl} onChange={(v) => audio({ autoGainControl: v })} label="Automatic gain control" />
      </Section>
      <Section title="Camera preview">
        <div className="aspect-video w-full max-w-md overflow-hidden rounded-xl border border-line bg-black">{preview && <video ref={video} autoPlay muted playsInline className="size-full -scale-x-100 object-cover" />}</div>
        <Button
          variant="secondary"
          onClick={async () => {
            if (preview) return setPreview(null);
            try {
              setPreview(await openDevice('video', s.audio.videoDeviceId));
              setMediaError(null);
            } catch {
              setMediaError('Could not open the camera.');
            }
          }}
        >
          {preview ? 'Stop preview' : 'Test camera'}
        </Button>
      </Section>
    </>
  );
}

function updateText(u: DesktopUpdateState): string {
  switch (u.status) {
    case 'checking':
      return 'Checking for updates…';
    case 'downloading':
      return `Downloading version ${u.version ?? ''}${u.percent ? ` (${u.percent}%)` : ''}…`;
    case 'ready':
      return `Version ${u.version ?? ''} is ready. It installs when you restart Spawnwire.`;
    case 'up-to-date':
      return 'You have the latest version.';
    case 'error':
      return u.message ?? 'Could not check for updates.';
    case 'disabled':
      return 'Updates are turned off in development builds.';
    default:
      return 'Spawnwire checks for updates automatically.';
  }
}

/** Preferences for this computer, stored by the Windows app rather than on the account. */
function DesktopApp() {
  const bridge = desktop!;
  const [settings, setSettings] = useState<DesktopSettings | null>(null);
  const [update, setUpdate] = useState<DesktopUpdateState | null>(null);
  useEffect(() => {
    void bridge.getSettings().then(setSettings);
    void bridge.getUpdateState().then(setUpdate);
    return bridge.onUpdateState(setUpdate);
  }, [bridge]);

  if (!settings) return <Spinner />;
  const change = (patch: Partial<DesktopSettings>) => {
    setSettings({ ...settings, ...patch });
    void bridge.updateSettings(patch).then(setSettings);
  };
  const busy = update?.status === 'checking' || update?.status === 'downloading';
  return (
    <>
      <Section title="Closing the window">
        <Switch
          checked={settings.closeToTray}
          onChange={(closeToTray) => change({ closeToTray })}
          label="Keep Spawnwire running in the notification area"
          description="The close button hides the window, so you still get messages and calls. Quit from the tray icon."
        />
      </Section>
      <Section title="Startup">
        <Switch checked={settings.launchAtStartup} onChange={(launchAtStartup) => change({ launchAtStartup })} label="Open Spawnwire when you sign in to Windows" />
        <Switch
          checked={settings.startMinimized}
          disabled={!settings.launchAtStartup}
          onChange={(startMinimized) => change({ startMinimized })}
          label="Start in the notification area"
          description="Spawnwire starts hidden and you open it from the tray icon."
        />
      </Section>
      <Section title="Taskbar">
        <Switch checked={settings.flashFrame} onChange={(flashFrame) => change({ flashFrame })} label="Flash the taskbar button for new notifications" />
      </Section>
      <Section title="Updates" description={`Spawnwire for Windows, version ${bridge.version}.`}>
        {update && <p className="mb-3 text-sm text-muted">{updateText(update)}</p>}
        {update?.status === 'ready' ? (
          <Button size="sm" onClick={() => bridge.installUpdate()}>
            Restart and update
          </Button>
        ) : (
          update?.status !== 'disabled' && (
            <Button variant="secondary" size="sm" loading={busy} disabled={busy} onClick={() => void bridge.checkForUpdates().then(setUpdate)}>
              Check for updates
            </Button>
          )
        )}
      </Section>
    </>
  );
}

function Keybinds() {
  return (
    <dl className="divide-y divide-line rounded-lg border border-line">
      {SHORTCUTS.map((s) => (
        <div key={s.keys} className="flex items-center justify-between gap-4 px-4 py-3">
          <dt className="text-sm text-fg">{s.action}</dt>
          <dd>
            <Kbd>{s.keys}</Kbd>
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function UserSettings({ section }: { section?: string }) {
  return (
    <SettingsLayout
      title="User settings"
      initial={section}
      sections={[
        { id: 'account', label: 'My account', group: 'User settings', render: () => <Account /> },
        { id: 'profile', label: 'Profile', group: 'User settings', render: () => <Profile /> },
        { id: 'privacy', label: 'Privacy and safety', group: 'User settings', render: () => <Privacy /> },
        { id: 'standing', label: 'Account standing', group: 'User settings', render: () => <AccountStandingSection /> },
        { id: 'notifications', label: 'Notifications', group: 'App settings', render: () => <Notifications /> },
        { id: 'appearance', label: 'Appearance', group: 'App settings', render: () => <Appearance /> },
        { id: 'accessibility', label: 'Accessibility', group: 'App settings', render: () => <AccessibilitySection /> },
        { id: 'voice', label: 'Voice and video', group: 'App settings', render: () => <VoiceVideo /> },
        { id: 'keybinds', label: 'Keyboard shortcuts', group: 'App settings', render: () => <Keybinds /> },
        ...(desktop ? [{ id: 'desktop', label: 'Windows app', group: 'App settings', render: () => <DesktopApp /> }] : []),
        {
          id: 'developer',
          label: 'Developer Portal',
          group: 'Developers',
          render: () => (
            <Suspense fallback={<Spinner />}>
              <DeveloperPortal />
            </Suspense>
          ),
        },
        { id: 'logout', label: 'Log out', group: ' ', danger: true, onSelect: () => { ui.close(); void logout(); } },
      ]}
    />
  );
}
